import { useEffect } from 'react';
import { Component } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Lenis from 'lenis';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Cursor from '@/components/Cursor';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Solutions from '@/pages/Solutions';
import HealthcareRevenue from '@/pages/services/HealthcareRevenue';
import EmployeeBenefits from '@/pages/services/EmployeeBenefits';
import TechnologyCosts from '@/pages/services/TechnologyCosts';
import ShippingCosts from '@/pages/services/ShippingCosts';
import AiMarketing from '@/pages/services/AiMarketing';
import OurApproach from '@/pages/OurApproach';
import Contact from '@/pages/Contact';
import Privacy from '@/pages/Privacy';
import '@/App.css';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="wrap" style={{ padding: '120px 24px 140px' }}>
          <h1 className="h2">Something went wrong.</h1>
          <p className="lede mt-4">
            Please refresh the page. If the problem continues, call us at 615-497-2166.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, search]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function NotFound() {
  return (
    <div className="wrap section" data-testid="not-found-page">
      <p className="eyebrow">Page not found</p>
      <h1 className="h1 mt-6">This page doesn't exist.</h1>
      <p className="lede mt-6">
        The link may be out of date. Start from the homepage, or explore our solutions.
      </p>
      <div className="mt-10 flex gap-6">
        <Link to="/" className="btn btn--solid" data-testid="not-found-home-link">
          Back to Home
        </Link>
        <Link to="/solutions" className="link-arrow" data-testid="not-found-solutions-link">
          Explore Our Solutions
          <span className="ar">→</span>
        </Link>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <div className="grain" aria-hidden="true" />
          <Cursor />
          <SmoothScroll />
          <ScrollManager />
          <Header />
          <main id="main">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/healthcare-revenue" element={<HealthcareRevenue />} />
              <Route path="/solutions/employee-benefits" element={<EmployeeBenefits />} />
              <Route path="/solutions/technology-costs" element={<TechnologyCosts />} />
              <Route path="/solutions/shipping-costs" element={<ShippingCosts />} />
              <Route path="/solutions/ai-marketing" element={<AiMarketing />} />
              <Route path="/our-approach" element={<OurApproach />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </ErrorBoundary>
      </BrowserRouter>
    </HelmetProvider>
  );
}
